import {
	CopyOutlined,
	DeleteOutlined,
	EditOutlined,
	PlusOutlined,
	TagOutlined,
} from '@ant-design/icons'
import {
	AutoComplete,
	Button,
	Form,
	Input,
	InputNumber,
	Modal,
	Popconfirm,
	Select,
	Space,
	Table,
	Tabs,
	Tag,
	Tooltip,
	message,
} from 'antd'
import { useCallback, useEffect, useState } from 'react'
import api from '../../api/index.api'
import { ListPageIntro, ListPageShell } from '../../common'
import './VoucherList.scss'

const { TabPane } = Tabs

const SUBSCRIPTION_OPTIONS = [
	{ value: 'PRO_1_MONTHS', label: 'PRO 1 month' },
	{ value: 'PRO_3_MONTHS', label: 'PRO 3 months' },
	{ value: 'PRO_6_MONTHS', label: 'PRO 6 months' },
	{ value: 'PRO', label: 'PRO (1 year)' },
	{ value: 'PRO_FOREVER', label: 'PRO Forever' },
]

const PREFIX_HINT =
	'Letters, numbers, hyphen, underscore. Codes become PREFIX01, PREFIX02, …'

const copyText = async text => {
	const value = String(text || '')
	if (!value) return
	try {
		if (navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(value)
		} else {
			const el = document.createElement('textarea')
			el.value = value
			document.body.appendChild(el)
			el.select()
			document.execCommand('copy')
			document.body.removeChild(el)
		}
		message.success('Copied')
	} catch (err) {
		message.error(err?.message || 'Could not copy')
	}
}

const formatTime = value => (value ? new Date(value).toLocaleString() : '—')

const matchGroup = (groups, raw) => {
	const value = String(raw || '')
		.trim()
		.toLowerCase()
	if (!value) return null
	return groups.find(g => String(g.groupCode || '').toLowerCase() === value) || null
}

export const VoucherList = () => {
	const [createForm] = Form.useForm()
	const [notesForm] = Form.useForm()
	const [activeTab, setActiveTab] = useState('groups')

	const [groups, setGroups] = useState([])
	const [groupsTotal, setGroupsTotal] = useState(0)
	const [groupsLoading, setGroupsLoading] = useState(false)
	const [groupPage, setGroupPage] = useState(0)
	const [groupPageSize, setGroupPageSize] = useState(20)
	const [groupQuery, setGroupQuery] = useState('')

	const [codes, setCodes] = useState([])
	const [codesTotal, setCodesTotal] = useState(0)
	const [codesLoading, setCodesLoading] = useState(false)
	const [codePage, setCodePage] = useState(0)
	const [codePageSize, setCodePageSize] = useState(20)
	const [codeQuery, setCodeQuery] = useState('')
	const [groupFilter, setGroupFilter] = useState(undefined)
	const [subscriptionFilter, setSubscriptionFilter] = useState(undefined)
	const [activatedFilter, setActivatedFilter] = useState(undefined)

	const [groupOptions, setGroupOptions] = useState([])
	const [matchedGroup, setMatchedGroup] = useState(null)
	const [createVisible, setCreateVisible] = useState(false)
	const [creating, setCreating] = useState(false)
	const [createdCodes, setCreatedCodes] = useState([])

	const [notesTarget, setNotesTarget] = useState(null)
	const [savingNotes, setSavingNotes] = useState(false)

	const loadGroups = useCallback(async () => {
		setGroupsLoading(true)
		try {
			const rs = await api.voucherAdminApi.listGroups({
				page: groupPage,
				pageSize: groupPageSize,
				q: groupQuery.trim() || undefined,
			})
			setGroups(Array.isArray(rs?.data) ? rs.data : [])
			setGroupsTotal(Number(rs?.total) || 0)
		} catch (err) {
			message.error(err?.message || 'Could not load voucher groups.')
		} finally {
			setGroupsLoading(false)
		}
	}, [groupPage, groupPageSize, groupQuery])

	const loadCodes = useCallback(async () => {
		setCodesLoading(true)
		try {
			const rs = await api.voucherAdminApi.list({
				page: codePage,
				pageSize: codePageSize,
				q: codeQuery.trim() || undefined,
				group: groupFilter || undefined,
				subscriptionCode: subscriptionFilter || undefined,
				activated: activatedFilter,
			})
			setCodes(Array.isArray(rs?.data) ? rs.data : [])
			setCodesTotal(Number(rs?.total) || 0)
		} catch (err) {
			message.error(err?.message || 'Could not load voucher codes.')
		} finally {
			setCodesLoading(false)
		}
	}, [
		codePage,
		codePageSize,
		codeQuery,
		groupFilter,
		subscriptionFilter,
		activatedFilter,
	])

	const loadGroupOptions = useCallback(async () => {
		try {
			const rs = await api.voucherAdminApi.listGroups({
				page: 0,
				pageSize: 200,
			})
			setGroupOptions(Array.isArray(rs?.data) ? rs.data : [])
		} catch (err) {
			message.error(err?.message || 'Could not load group options.')
		}
	}, [])

	useEffect(() => {
		loadGroups()
	}, [loadGroups])

	useEffect(() => {
		loadCodes()
	}, [loadCodes])

	useEffect(() => {
		loadGroupOptions()
	}, [loadGroupOptions])

	const applyGroupChange = value => {
		const group = matchGroup(groupOptions, value)
		setMatchedGroup(group)
		if (group) {
			createForm.setFieldsValue({
				subscriptionCode: group.subscriptionCode,
				prefix: createForm.getFieldValue('prefix') || group.groupCode,
			})
		}
	}

	const openCreate = group => {
		createForm.resetFields()
		setMatchedGroup(group || null)
		createForm.setFieldsValue({
			count: 10,
			subscriptionCode: group?.subscriptionCode,
			groupVoucherCode: group?.groupCode,
			prefix: group?.groupCode,
			notes: group?.notes,
		})
		setCreateVisible(true)
	}

	const handleCreate = async () => {
		try {
			const values = await createForm.validateFields()
			setCreating(true)
			const payload = {
				subscriptionCode: values.subscriptionCode,
				count: Number(values.count),
				prefix: String(values.prefix || '').trim(),
				notes: String(values.notes || '').trim(),
			}
			const groupCode = String(values.groupVoucherCode || '').trim()
			if (groupCode) payload.groupVoucherCode = groupCode
			const rs = await api.voucherAdminApi.create(payload)
			const created = Array.isArray(rs?.data) ? rs.data : []
			setCreatedCodes(created)
			setCreateVisible(false)
			createForm.resetFields()
			setMatchedGroup(null)
			message.success(`Created ${created.length} voucher code(s).`)
			loadGroups()
			loadCodes()
			loadGroupOptions()
		} catch (err) {
			if (err?.errorFields) return
			message.error(err?.message || 'Could not create voucher codes.')
		} finally {
			setCreating(false)
		}
	}

	const openNotes = (type, record) => {
		setNotesTarget({
			type,
			id: record.id,
			title: type === 'group' ? record.groupCode : record.voucherCode,
		})
		notesForm.setFieldsValue({ notes: record.notes || '' })
	}

	const handleSaveNotes = async () => {
		try {
			const values = await notesForm.validateFields()
			setSavingNotes(true)
			const notes = String(values.notes || '').trim()
			if (notesTarget.type === 'group') {
				await api.voucherAdminApi.updateGroupNotes(notesTarget.id, notes)
			} else {
				await api.voucherAdminApi.updateNotes(notesTarget.id, notes)
			}
			message.success('Notes updated.')
			setNotesTarget(null)
			notesForm.resetFields()
			loadGroups()
			loadCodes()
		} catch (err) {
			if (err?.errorFields) return
			message.error(err?.message || 'Could not update notes.')
		} finally {
			setSavingNotes(false)
		}
	}

	const handleDeleteCode = async id => {
		try {
			await api.voucherAdminApi.remove(id)
			message.success('Voucher deleted.')
			loadCodes()
			loadGroups()
		} catch (err) {
			message.error(err?.message || 'Could not delete voucher.')
		}
	}

	const viewGroupCodes = groupCode => {
		setGroupFilter(groupCode)
		setCodePage(0)
		setActiveTab('codes')
	}

	const groupColumns = [
			{
				title: 'Group code',
				dataIndex: 'groupCode',
				key: 'groupCode',
				render: value => (
					<Space>
						<code>{value}</code>
						<Tooltip title="Copy group code">
							<Button
								type="text"
								size="small"
								icon={<CopyOutlined />}
								onClick={() => copyText(value)}
							/>
						</Tooltip>
					</Space>
				),
			},
			{
				title: 'Subscription',
				dataIndex: 'subscriptionCode',
				key: 'subscriptionCode',
				width: 160,
				render: value => <Tag color="blue">{value}</Tag>,
			},
			{
				title: 'Notes',
				dataIndex: 'notes',
				key: 'notes',
				ellipsis: true,
				render: value => value || '—',
			},
			{
				title: 'Total',
				dataIndex: 'totalCodes',
				key: 'totalCodes',
				width: 80,
			},
			{
				title: 'Unused',
				dataIndex: 'unusedCount',
				key: 'unusedCount',
				width: 90,
			},
			{
				title: 'Activated',
				dataIndex: 'activatedCount',
				key: 'activatedCount',
				width: 100,
			},
			{
				title: 'Created',
				dataIndex: 'createdAt',
				key: 'createdAt',
				width: 180,
				render: formatTime,
			},
			{
				title: 'Actions',
				key: 'actions',
				width: 220,
				fixed: 'right',
				render: (_, record) => (
					<Space size={4}>
						<Button size="small" onClick={() => viewGroupCodes(record.groupCode)}>
							View codes
						</Button>
						<Button
							size="small"
							onClick={() => openCreate(record)}
						>
							Add codes
						</Button>
						<Tooltip title="Edit notes">
							<Button
								size="small"
								icon={<EditOutlined />}
								onClick={() => openNotes('group', record)}
							/>
						</Tooltip>
					</Space>
				),
			},
		]

	const codeColumns = [
			{
				title: 'Voucher code',
				dataIndex: 'voucherCode',
				key: 'voucherCode',
				render: value => (
					<Space>
						<code>{value}</code>
						<Tooltip title="Copy code">
							<Button
								type="text"
								size="small"
								icon={<CopyOutlined />}
								onClick={() => copyText(value)}
							/>
						</Tooltip>
					</Space>
				),
			},
			{
				title: 'Group',
				dataIndex: 'groupVoucherCode',
				key: 'groupVoucherCode',
				width: 160,
				render: value => value || '—',
			},
			{
				title: 'Subscription',
				dataIndex: 'subscriptionCode',
				key: 'subscriptionCode',
				width: 150,
				render: value => <Tag color="blue">{value}</Tag>,
			},
			{
				title: 'Status',
				dataIndex: 'activated',
				key: 'activated',
				width: 110,
				render: value =>
					value ? (
						<Tag color="green">Activated</Tag>
					) : (
						<Tag>Unused</Tag>
					),
			},
			{
				title: 'Order ID',
				dataIndex: 'orderID',
				key: 'orderID',
				width: 100,
				render: value => value || '—',
			},
			{
				title: 'Redeemed by',
				key: 'redeemedBy',
				width: 220,
				ellipsis: true,
				render: (_, record) => {
					if (!record.activated) return '—'
					return record.redeemedEmail || record.redeemedName || '—'
				},
			},
			{
				title: 'Notes',
				dataIndex: 'notes',
				key: 'notes',
				ellipsis: true,
				render: value => value || '—',
			},
			{
				title: 'Created',
				dataIndex: 'createdAt',
				key: 'createdAt',
				width: 180,
				render: formatTime,
			},
			{
				title: 'Actions',
				key: 'actions',
				width: 120,
				fixed: 'right',
				render: (_, record) => (
					<Space size={4}>
						<Tooltip
							title={
								record.activated
									? 'Activated codes cannot be edited'
									: 'Edit notes'
							}
						>
							<span>
								<Button
									size="small"
									icon={<EditOutlined />}
									disabled={record.activated}
									onClick={() => openNotes('code', record)}
								/>
							</span>
						</Tooltip>
						<Tooltip
							title={
								record.activated
									? 'Activated codes cannot be deleted'
									: 'Delete'
							}
						>
							<span>
								<Popconfirm
									title="Delete this unused voucher?"
									okText="Delete"
									okButtonProps={{ danger: true }}
									onConfirm={() => handleDeleteCode(record.id)}
									disabled={record.activated}
								>
									<Button
										size="small"
										danger
										icon={<DeleteOutlined />}
										disabled={record.activated}
									/>
								</Popconfirm>
							</span>
						</Tooltip>
					</Space>
				),
			},
		]

	const createdCodeLines = createdCodes.map(item => item.voucherCode).join('\n')

	return (
		<ListPageShell className="voucher-list">
			<ListPageIntro
				icon={TagOutlined}
				title="Voucher management"
				subtitle="Create campaign codes in bulk, optionally share one group code, and track which codes have been activated."
			/>
			<div className="voucher-list__toolbar">
				<Button
					type="primary"
					icon={<PlusOutlined />}
					onClick={() => openCreate(null)}
				>
					Create codes
				</Button>
			</div>

			<Tabs activeKey={activeTab} onChange={setActiveTab}>
				<TabPane tab={`Groups (${groupsTotal})`} key="groups">
					<div className="voucher-list__toolbar">
						<Input.Search
							allowClear
							placeholder="Search group code, notes, subscription"
							onSearch={value => {
								setGroupPage(0)
								setGroupQuery(value || '')
							}}
							style={{ width: 320 }}
						/>
					</div>
					<Table
						rowKey="id"
						loading={groupsLoading}
						columns={groupColumns}
						dataSource={groups}
						scroll={{ x: 1100 }}
						pagination={{
							current: groupPage + 1,
							pageSize: groupPageSize,
							total: groupsTotal,
							showSizeChanger: true,
							showTotal: t => `${t} group(s)`,
							onChange: (nextPage, nextSize) => {
								setGroupPage(nextPage - 1)
								setGroupPageSize(nextSize)
							},
						}}
					/>
				</TabPane>
				<TabPane tab={`All codes (${codesTotal})`} key="codes">
					<div className="voucher-list__toolbar">
						<Space wrap>
							<Input.Search
								allowClear
								placeholder="Search code, group, notes, email"
								onSearch={value => {
									setCodePage(0)
									setCodeQuery(value || '')
								}}
								style={{ width: 280 }}
							/>
							<Select
								allowClear
								showSearch
								placeholder="Group code"
								style={{ width: 180 }}
								value={groupFilter}
								onChange={value => {
									setCodePage(0)
									setGroupFilter(value)
								}}
								options={groupOptions.map(group => ({
									value: group.groupCode,
									label: group.groupCode,
								}))}
							/>
							<Select
								allowClear
								placeholder="Subscription"
								style={{ width: 170 }}
								value={subscriptionFilter}
								onChange={value => {
									setCodePage(0)
									setSubscriptionFilter(value)
								}}
								options={SUBSCRIPTION_OPTIONS}
							/>
							<Select
								allowClear
								placeholder="Status"
								style={{ width: 140 }}
								value={activatedFilter}
								onChange={value => {
									setCodePage(0)
									setActivatedFilter(value)
								}}
								options={[
									{ value: 'false', label: 'Unused' },
									{ value: 'true', label: 'Activated' },
								]}
							/>
						</Space>
					</div>
					<Table
						rowKey="id"
						loading={codesLoading}
						columns={codeColumns}
						dataSource={codes}
						scroll={{ x: 1400 }}
						pagination={{
							current: codePage + 1,
							pageSize: codePageSize,
							total: codesTotal,
							showSizeChanger: true,
							showTotal: t => `${t} code(s)`,
							onChange: (nextPage, nextSize) => {
								setCodePage(nextPage - 1)
								setCodePageSize(nextSize)
							},
						}}
					/>
				</TabPane>
			</Tabs>

			<Modal
				title="Create voucher codes"
				visible={createVisible}
				onCancel={() => {
					setCreateVisible(false)
					setMatchedGroup(null)
				}}
				onOk={handleCreate}
				okText="Create"
				confirmLoading={creating}
				destroyOnClose
			>
				<Form form={createForm} layout="vertical">
					<Form.Item
						label="Subscription"
						name="subscriptionCode"
						rules={[{ required: true, message: 'Select a subscription' }]}
					>
						<Select
							options={SUBSCRIPTION_OPTIONS}
							placeholder="e.g. PRO 3 months"
							disabled={Boolean(matchedGroup)}
						/>
					</Form.Item>
					<Form.Item
						label="How many codes"
						name="count"
						rules={[{ required: true, message: 'Enter a count' }]}
					>
						<InputNumber min={1} max={500} style={{ width: '100%' }} />
					</Form.Item>
					<Form.Item
						label="Prefix"
						name="prefix"
						extra={PREFIX_HINT}
						rules={[{ required: true, message: 'Enter a prefix' }]}
					>
						<Input placeholder="UNIABC" />
					</Form.Item>
					<Form.Item
						label="Group voucher code (optional)"
						name="groupVoucherCode"
						extra="Shared campaign code. Students can redeem this one code from the unused pool. Pick an existing group to append more codes."
					>
						<AutoComplete
							allowClear
							options={groupOptions.map(group => ({
								value: group.groupCode,
								label: `${group.groupCode} (${group.subscriptionCode})`,
							}))}
							placeholder="e.g. UNI-ABC"
							onChange={applyGroupChange}
							filterOption={(input, option) =>
								String(option?.value || '')
									.toLowerCase()
									.includes(String(input || '').toLowerCase())
							}
						/>
					</Form.Item>
					{matchedGroup && (
						<Tag color="processing" style={{ marginBottom: 12 }}>
							Appending to existing group {matchedGroup.groupCode} (
							{matchedGroup.unusedCount} unused / {matchedGroup.totalCodes} total)
						</Tag>
					)}
					<Form.Item
						label="Notes"
						name="notes"
						rules={[
							{
								max: 128,
								message: 'Notes must be 128 characters or fewer',
							},
						]}
					>
						<Input.TextArea
							placeholder="e.g. University ABC event — 50 x 3 months"
							autoSize={{ minRows: 2, maxRows: 4 }}
						/>
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title={notesTarget ? `Edit notes — ${notesTarget.title}` : 'Edit notes'}
				visible={Boolean(notesTarget)}
				onCancel={() => {
					setNotesTarget(null)
					notesForm.resetFields()
				}}
				onOk={handleSaveNotes}
				okText="Save"
				confirmLoading={savingNotes}
				destroyOnClose
			>
				<Form form={notesForm} layout="vertical">
					<Form.Item
						name="notes"
						rules={[
							{
								max: 128,
								message: 'Notes must be 128 characters or fewer',
							},
						]}
					>
						<Input.TextArea autoSize={{ minRows: 3, maxRows: 6 }} />
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title={`Created ${createdCodes.length} code(s)`}
				visible={createdCodes.length > 0}
				onCancel={() => setCreatedCodes([])}
				footer={[
					<Button key="copy" icon={<CopyOutlined />} onClick={() => copyText(createdCodeLines)}>
						Copy all
					</Button>,
					<Button key="close" type="primary" onClick={() => setCreatedCodes([])}>
						Done
					</Button>,
				]}
			>
				<pre className="voucher-list__codes">{createdCodeLines}</pre>
			</Modal>
		</ListPageShell>
	)
}
